import { applyActionCode, createUserWithEmailAndPassword, EmailAuthProvider, fetchSignInMethodsForEmail, getMultiFactorResolver, GoogleAuthProvider, linkWithPopup, multiFactor, onAuthStateChanged, reauthenticateWithCredential, reauthenticateWithPopup, reload, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, signOut, TotpMultiFactorGenerator, updateProfile, verifyPasswordResetCode, type MultiFactorResolver, type TotpSecret, type User } from "firebase/auth";
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { defineStore } from "pinia";
import * as QRCode from 'qrcode';
import { buildResetPasswordPath, buildVerifyEmailPath } from '~/lib/authRedirects';
import { publicImmutableUploadMetadata } from '~/lib/storageUploadMetadata';

let authInitPromise: Promise<any | null> | null = null;
let authListenerBound = false;
let pendingTotpSecret: TotpSecret | null = null;
type AuthMfaStatus = {
    projectAvailable: boolean,
    projectEnabled: boolean,
    totpEnabled: boolean,
    enforcedForTenantUsers: boolean,
    hasTotpEnrolled: boolean,
    factorUid: string | null,
    factorDisplayName: string | null,
    factorEnrolledAt: string | null,
    secondFactor: string | null,
    secondFactorIdentifier: string | null,
};

const createDefaultMfaStatus = (): AuthMfaStatus => ({
    projectAvailable: false,
    projectEnabled: false,
    totpEnabled: false,
    enforcedForTenantUsers: false,
    hasTotpEnrolled: false,
    factorUid: null,
    factorDisplayName: null,
    factorEnrolledAt: null,
    secondFactor: null,
    secondFactorIdentifier: null,
});

function normalizeOtp(code: string): string {
    return String(code || '').replace(/\D/g, '').trim();
}

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null as any | null,
        loginError: null as string | null,
        signupError: null as string | null,
        signupErrorCode: null as string | null,
        initialized: false,
        mfaStatus: createDefaultMfaStatus(),
        mfaStatusLoadedForUid: null as string | null,
        mfaStatusLoading: false,
        mfaStatusError: null as string | null,
        mfaResolver: null as MultiFactorResolver | null,
        mfaChallengeHintUid: null as string | null,
        mfaChallengeHintLabel: null as string | null,
        mfaChallengeRequired: false,
        mfaEnrollmentQrCodeDataUrl: null as string | null,
        mfaEnrollmentManualKey: null as string | null,
        mfaEnrollmentIssuer: null as string | null,
        mfaEnrollmentAccountName: null as string | null,
        mfaEnrollmentDisplayName: 'Authenticator app' as string,
    }),

    actions: {

        setupAuthListener() {
            const { $auth } = useNuxtApp();
            if (authInitPromise) {
                return authInitPromise;
            }

            if (!$auth || !($auth as any).app) {
                this.initialized = true;
                authInitPromise = Promise.resolve(null);
                return authInitPromise;
            }

            if (!authListenerBound) {
                onAuthStateChanged($auth, (user) => {
                    const previousUid = this.user?.uid || null;
                    const nextUid = user?.uid || null;

                    if (previousUid !== nextUid) {
                        this.resetMfaStatus();
                        this.clearMfaChallenge();
                        this.clearMfaEnrollmentDraft();
                    }

                    this.user = user;
                    this.initialized = true;
                });
                authListenerBound = true;
            }

            const authStateReady = typeof ($auth as Auth & { authStateReady?: () => Promise<void> }).authStateReady === 'function'
                ? ($auth as Auth & { authStateReady: () => Promise<void> }).authStateReady().then(() => $auth.currentUser)
                : new Promise<User | null>((resolve) => {
                    const unsubscribe = onAuthStateChanged($auth, (user) => {
                        unsubscribe();
                        resolve(user);
                    });
                });

            const fallbackAfterDelay = new Promise<User | null>((resolve) => {
                setTimeout(() => {
                    if (!this.initialized) {
                        console.warn('[auth] Initial auth state did not settle in time. Falling back to currentUser.');
                        this.user = $auth.currentUser;
                        this.initialized = true;
                    }
                    resolve($auth.currentUser);
                }, 3000);
            });

            authInitPromise = Promise.race([
                authStateReady.then((user) => {
                    this.user = user;
                    this.initialized = true;
                    return user;
                }),
                fallbackAfterDelay,
            ]);

            return authInitPromise;
        },

        setupAuthListner() {
            return this.setupAuthListener();
        },

        async awaitAuthReady() {
            if (this.initialized) {
                return this.user;
            }

            return await this.setupAuthListener();
        },

        getEmailVerificationContinueUrl(redirectPath = '/onboarding', tenantId?: string | null) {
            const config = useRuntimeConfig();
            const configuredAppDomain = String(config.public.appDomain || '').trim().replace(/\/$/, '');

            const baseOrigin = configuredAppDomain
                ? (/^https?:\/\//.test(configuredAppDomain) ? configuredAppDomain : `https://${configuredAppDomain}`)
                : (typeof window !== 'undefined' ? window.location.origin : '');

            if (!baseOrigin) {
                return '';
            }

            return `${baseOrigin}${buildVerifyEmailPath({
                redirect: redirectPath,
                tenantId,
            })}`;
        },

        getPasswordResetContinueUrl(redirectPath = '/auth/login', tenantId?: string | null) {
            if (typeof window === 'undefined') {
                return '';
            }

            return `${window.location.origin}${buildResetPasswordPath({
                redirect: redirectPath,
                tenantId,
            })}`;
        },

        async sendVerificationEmail(options?: { redirectPath?: string, tenantId?: string | null }) {
            const { $auth } = useNuxtApp();
            const readyUser = await this.awaitAuthReady();
            const activeUser = ($auth.currentUser || readyUser || this.user) as User | null;

            if (!activeUser) {
                throw new Error('You need to be signed in to verify your email.');
            }

            await reload(activeUser);
            this.user = $auth.currentUser || activeUser;

            if (this.user?.emailVerified) {
                throw new Error('This email address is already verified.');
            }

            const token = await this.user.getIdToken(true);

            await $fetch('/api/auth/send-verification-email', {
                method: 'POST',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: {
                    redirectPath: options?.redirectPath || '/onboarding',
                    tenantId: options?.tenantId || null,
                },
            });
        },

        async refreshUser() {
            const { $auth } = useNuxtApp();
            const currentUser = $auth.currentUser;

            if (!currentUser) {
                this.user = null;
                return null;
            }

            await reload(currentUser);
            this.user = $auth.currentUser;
            return this.user;
        },

        async applyEmailVerificationCode(code: string) {
            const { $auth } = useNuxtApp();
            await applyActionCode($auth, code);
            await this.refreshUser();
            return this.user;
        },

        async sendPasswordReset(email: string, options?: { redirectPath?: string, tenantId?: string | null }) {
            const { $auth } = useNuxtApp();
            const normalizedEmail = String(email || '').trim().toLowerCase();

            if (!normalizedEmail) {
                throw new Error('Enter your email address to reset your password.');
            }

            const tenantId = String(options?.tenantId || '').trim();
            if (tenantId) {
                await $fetch('/api/auth/request-client-password-reset', {
                    method: 'POST',
                    body: {
                        email: normalizedEmail,
                        tenantId,
                        redirectPath: options?.redirectPath || `/auth/client-login?tenantId=${encodeURIComponent(tenantId)}`,
                    },
                });
                return;
            }

            const continueUrl = this.getPasswordResetContinueUrl(options?.redirectPath || '/auth/login');
            await sendPasswordResetEmail($auth, normalizedEmail, continueUrl ? { url: continueUrl } : undefined);
        },

        async verifyPasswordReset(code: string) {
            const { $auth } = useNuxtApp();
            return await verifyPasswordResetCode($auth, code);
        },

        async confirmPasswordReset(code: string, newPassword: string) {
            if (String(newPassword || '').length < 6) {
                throw new Error('Password must be at least 6 characters.');
            }

            await $fetch('/api/auth/confirm-password-reset', {
                method: 'POST',
                body: { code, newPassword },
            });
        },

        resetMfaStatus() {
            this.mfaStatus = createDefaultMfaStatus();
            this.mfaStatusLoadedForUid = null;
            this.mfaStatusLoading = false;
            this.mfaStatusError = null;
        },

        clearMfaChallenge() {
            this.mfaResolver = null;
            this.mfaChallengeHintUid = null;
            this.mfaChallengeHintLabel = null;
            this.mfaChallengeRequired = false;
        },

        clearMfaEnrollmentDraft() {
            pendingTotpSecret = null;
            this.mfaEnrollmentQrCodeDataUrl = null;
            this.mfaEnrollmentManualKey = null;
            this.mfaEnrollmentIssuer = null;
            this.mfaEnrollmentAccountName = null;
            this.mfaEnrollmentDisplayName = 'Authenticator app';
        },

        async loadMfaStatus(force = false) {
            const { $auth } = useNuxtApp();
            const currentUser = ($auth.currentUser || this.user) as User | null;

            if (!currentUser) {
                this.resetMfaStatus();
                return this.mfaStatus;
            }

            if (!force && this.mfaStatusLoadedForUid === currentUser.uid) {
                return this.mfaStatus;
            }

            this.mfaStatusLoading = true;
            this.mfaStatusError = null;

            try {
                const token = await currentUser.getIdToken(force);
                const response = await $fetch<{
                    project?: {
                        available?: boolean,
                        enabled?: boolean,
                        totpEnabled?: boolean,
                        enforced?: boolean,
                    },
                    user?: {
                        hasTotpEnrolled?: boolean,
                        factorUid?: string | null,
                        factorDisplayName?: string | null,
                        factorEnrolledAt?: string | null,
                    },
                    session?: {
                        secondFactor?: string | null,
                        secondFactorIdentifier?: string | null,
                    },
                }>('/api/auth/mfa/status', {
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                });

                this.mfaStatus = {
                    projectAvailable: Boolean(response?.project?.available),
                    projectEnabled: Boolean(response?.project?.enabled),
                    totpEnabled: Boolean(response?.project?.totpEnabled),
                    enforcedForTenantUsers: Boolean(response?.project?.enforced),
                    hasTotpEnrolled: Boolean(response?.user?.hasTotpEnrolled),
                    factorUid: response?.user?.factorUid || null,
                    factorDisplayName: response?.user?.factorDisplayName || null,
                    factorEnrolledAt: response?.user?.factorEnrolledAt || null,
                    secondFactor: response?.session?.secondFactor || null,
                    secondFactorIdentifier: response?.session?.secondFactorIdentifier || null,
                };
                this.mfaStatusLoadedForUid = currentUser.uid;
                return this.mfaStatus;
            } catch (error: any) {
                this.mfaStatusError = error?.data?.statusMessage || error?.message || 'Unable to check two-factor authentication status right now.';
                this.mfaStatus = createDefaultMfaStatus();
                this.mfaStatusLoadedForUid = currentUser.uid;
                return this.mfaStatus;
            } finally {
                this.mfaStatusLoading = false;
            }
        },

        async beginTotpEnrollment(displayName = 'Authenticator app') {
            const { $auth } = useNuxtApp();
            const currentUser = ($auth.currentUser || this.user) as User | null;

            if (!currentUser) {
                throw new Error('You need to be signed in to set up two-factor authentication.');
            }

            const multiFactorSession = await multiFactor(currentUser).getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
            const issuer = useTenantStore().currentTenant?.name || 'Vuey';
            const accountName = String(currentUser.email || '').trim().toLowerCase() || 'unknownuser';
            const qrCodeDataUrl = await QRCode.toDataURL(secret.generateQrCodeUrl(accountName, issuer), {
                margin: 1,
                width: 256,
            });

            pendingTotpSecret = secret;
            this.mfaEnrollmentQrCodeDataUrl = qrCodeDataUrl;
            this.mfaEnrollmentManualKey = secret.secretKey;
            this.mfaEnrollmentIssuer = issuer;
            this.mfaEnrollmentAccountName = accountName;
            this.mfaEnrollmentDisplayName = String(displayName || 'Authenticator app').trim() || 'Authenticator app';

            return {
                qrCodeDataUrl,
                manualKey: secret.secretKey,
                issuer,
                accountName,
                codeLength: secret.codeLength,
                intervalSeconds: secret.codeIntervalSeconds,
                enrollmentCompletionDeadline: secret.enrollmentCompletionDeadline,
            };
        },

        async completeTotpEnrollment(code: string, displayName?: string) {
            const { $auth } = useNuxtApp();
            const currentUser = ($auth.currentUser || this.user) as User | null;
            const secret = pendingTotpSecret;
            const normalizedCode = normalizeOtp(code);

            if (!currentUser || !secret) {
                throw new Error('Start two-factor setup first before confirming your code.');
            }

            if (normalizedCode.length < 6) {
                throw new Error('Enter the 6-digit code from your authenticator app.');
            }

            const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, normalizedCode);
            await multiFactor(currentUser).enroll(
                assertion,
                String(displayName || this.mfaEnrollmentDisplayName || 'Authenticator app').trim() || 'Authenticator app',
            );

            this.clearMfaEnrollmentDraft();
            await this.refreshUser();
            await this.loadMfaStatus(true);
            return this.mfaStatus;
        },

        async unenrollTotp() {
            const { $auth } = useNuxtApp();
            const currentUser = ($auth.currentUser || this.user) as User | null;
            const factorUid = this.mfaStatus.factorUid;

            if (!currentUser || !factorUid) {
                throw new Error('There is no authenticator app linked to remove right now.');
            }

            await multiFactor(currentUser).unenroll(factorUid);
            await this.refreshUser();
            await this.loadMfaStatus(true);
            return this.mfaStatus;
        },

        captureMfaChallenge(error: any) {
            const code = String(error?.code || '').trim();

            if (code !== 'auth/multi-factor-auth-required') {
                return false;
            }

            const { $auth } = useNuxtApp();
            const resolver = getMultiFactorResolver($auth, error);
            const totpHint = resolver.hints.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) || resolver.hints[0];

            this.mfaResolver = resolver;
            this.mfaChallengeHintUid = totpHint?.uid || null;
            this.mfaChallengeHintLabel = totpHint?.displayName || 'your authenticator app';
            this.mfaChallengeRequired = true;
            this.loginError = 'Enter the 6-digit code from your authenticator app to finish signing in.';

            return true;
        },

        async completeTotpSignIn(code: string) {
            const normalizedCode = normalizeOtp(code);

            if (!this.mfaResolver || !this.mfaChallengeHintUid) {
                throw new Error('Start a sign-in challenge first before entering a code.');
            }

            if (normalizedCode.length < 6) {
                throw new Error('Enter the 6-digit code from your authenticator app.');
            }

            const assertion = TotpMultiFactorGenerator.assertionForSignIn(this.mfaChallengeHintUid, normalizedCode);
            const credential = await this.mfaResolver.resolveSignIn(assertion);

            this.clearMfaChallenge();
            this.loginError = null;
            await this.refreshUser();
            await this.loadMfaStatus(true);
            return credential.user;
        },

        getGoogleProvider(loginHint?: string) {
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({
                prompt: 'select_account',
                ...(loginHint ? { login_hint: loginHint } : {}),
            });
            return provider;
        },

        async upsertUserProfileFromAuthUser(authUser: User) {
            const { $db } = useNuxtApp();
            const userRef = doc($db, 'users', authUser.uid);
            const userSnap = await getDoc(userRef);
            const existing = userSnap.exists()
                ? userSnap.data() as {
                    email?: string,
                    displayName?: string,
                    fullName?: string,
                    avatarURL?: string,
                    tenants?: Array<{ tenantId: string; role: string }>,
                    createdAt?: unknown,
                    updatedAt?: unknown,
                    accountType?: 'team' | 'client',
                }
                : null;

            await setDoc(userRef, {
                ...(existing?.accountType ? { accountType: existing.accountType } : {}),
                email: String(existing?.email || authUser.email || '').trim().toLowerCase(),
                displayName: String(existing?.displayName || authUser.displayName || authUser.email?.split('@')[0] || 'User').trim(),
                fullName: String(existing?.fullName || authUser.displayName || authUser.email?.split('@')[0] || 'User').trim(),
                avatarURL: String(existing?.avatarURL || authUser.photoURL || '').trim(),
                tenants: Array.isArray(existing?.tenants) ? existing.tenants : [],
                createdAt: existing?.createdAt || new Date(),
                updatedAt: new Date(),
            }, { merge: true });
        },

        async signInWithGoogleMode(mode: 'login' | 'signup') {
            const { $auth } = useNuxtApp();
            this.clearMfaChallenge();

            if (mode === 'login') {
                this.loginError = null;
            } else {
                this.signupError = null;
                this.signupErrorCode = null;
            }

            try {
                const credential = await signInWithPopup($auth, this.getGoogleProvider());
                await this.upsertUserProfileFromAuthUser(credential.user);
                await this.refreshUser();
                await this.loadMfaStatus(true);
                return credential.user;
            } catch (error: any) {
                const code = String(error?.code || '').trim();
                const email = String(error?.customData?.email || '').trim().toLowerCase();

                if (this.captureMfaChallenge(error)) {
                    return null;
                }

                let message = error?.message || 'Google sign-in could not be completed.';

                if (code === 'auth/account-exists-with-different-credential') {
                    let signInMethods: string[] = [];
                    try {
                        signInMethods = email ? await fetchSignInMethodsForEmail($auth, email) : [];
                    } catch {
                        signInMethods = [];
                    }

                    if (signInMethods.includes('password')) {
                        message = 'This email already has a password-based Vuey account. Sign in with your password first, then link Google from Account > Security.';
                    } else {
                        message = 'This email already belongs to a Vuey account with a different sign-in method. Sign in to that account first, then link Google from Account > Security.';
                    }
                }

                if (mode === 'login') {
                    this.loginError = message;
                } else {
                    this.signupError = message;
                    this.signupErrorCode = code || null;
                }

                throw error;
            }
        },

        async loginWithGoogle() {
            return await this.signInWithGoogleMode('login');
        },

        async signupWithGoogle() {
            return await this.signInWithGoogleMode('signup');
        },

        async linkGoogleAccount() {
            const { $auth } = useNuxtApp();
            const currentUser = $auth.currentUser;

            if (!currentUser) {
                throw new Error('You need to be signed in to link Google.');
            }

            try {
                const result = await linkWithPopup(currentUser, this.getGoogleProvider(currentUser.email || undefined));
                await this.upsertUserProfileFromAuthUser(result.user);
                await this.refreshUser();
                return result.user;
            } catch (error: any) {
                const code = String(error?.code || '').trim();

                if (code === 'auth/provider-already-linked') {
                    throw new Error('Google is already linked to this Vuey account.');
                }

                if (code === 'auth/credential-already-in-use') {
                    throw new Error('That Google account is already linked to a different Vuey account.');
                }

                if (code === 'auth/account-exists-with-different-credential') {
                    throw new Error('That Google email already belongs to a different sign-in method. Sign in with that method first if you need to merge access.');
                }

                throw new Error(error?.message || 'Unable to link Google right now.');
            }
        },

        async reauthenticateWithPassword(password: string) {
            const { $auth } = useNuxtApp();
            const currentUser = ($auth.currentUser || this.user) as User | null;
            const email = String(currentUser?.email || '').trim().toLowerCase();

            if (!currentUser || !email) {
                throw new Error('You need to be signed in with an email address to confirm this change.');
            }

            if (!String(password || '').trim()) {
                throw new Error('Enter your password to continue.');
            }

            const credential = EmailAuthProvider.credential(email, password);
            await reauthenticateWithCredential(currentUser, credential);
            await this.refreshUser();
            return currentUser;
        },

        async reauthenticateWithGoogle() {
            const { $auth } = useNuxtApp();
            const currentUser = ($auth.currentUser || this.user) as User | null;

            if (!currentUser) {
                throw new Error('You need to be signed in to confirm this change.');
            }

            await reauthenticateWithPopup(
                currentUser,
                this.getGoogleProvider(currentUser.email || undefined),
            );
            await this.refreshUser();
            return currentUser;
        },

        async signup(email: string, password: string, displayName: string, avatarFile?: File | null) {
            const { $auth, $db, $storage } = useNuxtApp();
            this.signupError = null;
            this.signupErrorCode = null;
            this.clearMfaChallenge();

            try {
                const credential = await createUserWithEmailAndPassword($auth, email, password);
                const userId = credential.user.uid;
                let avatarURL = '';

                if (avatarFile) {
                    const filename = avatarFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                    const avatarRef = storageRef($storage, `users/${userId}/avatar/${Date.now()}-${filename}`);
                    await uploadBytes(avatarRef, avatarFile, publicImmutableUploadMetadata(avatarFile));
                    avatarURL = await getDownloadURL(avatarRef);
                }

                await updateProfile(credential.user, {
                    displayName,
                    ...(avatarURL ? { photoURL: avatarURL } : {}),
                });

                await setDoc(doc($db, 'users', userId), {
                    accountType: 'team',
                    email: email.toLowerCase(),
                    displayName,
                    fullName: displayName,
                    avatarURL,
                    tenants: [],
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });

                try {
                    await this.sendVerificationEmail({ redirectPath: '/onboarding' });
                } catch (verificationError) {
                    console.warn('Unable to send verification email after signup:', verificationError);
                }
            } catch (error: any) {
                this.signupErrorCode = String(error?.code || '').trim() || null;

                if (this.signupErrorCode === 'auth/email-already-in-use') {
                    this.signupError = 'This email already has an account. Log in to continue and create your workspace from that account.';
                    return;
                }

                this.signupError = error.message;
            }
        },

        async login(email: string, password: string) {
            const { $auth } = useNuxtApp();
            this.loginError = null;
            this.clearMfaChallenge();

            try {
                await signInWithEmailAndPassword($auth, email, password);
                await this.loadMfaStatus(true);
            } catch (error: any) {
                if (this.captureMfaChallenge(error)) {
                    return;
                }
                this.loginError = error.message;
            }
        },

        async logout() {
            const { $auth } = useNuxtApp();
            await signOut($auth);
            this.clearMfaChallenge();
            this.clearMfaEnrollmentDraft();
            this.resetMfaStatus();
        },
    },

    getters: {
        tenantMfaEnforced: (state) => state.mfaStatus.enforcedForTenantUsers,
        hasTotpEnrolled: (state) => state.mfaStatus.hasTotpEnrolled,
        hasSatisfiedTotpChallenge: (state) => state.mfaStatus.secondFactor === TotpMultiFactorGenerator.FACTOR_ID,
    },
});
